import React, { ComponentType, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ShowChartOutlined from '@mui/icons-material/ShowChartOutlined';
import { GRID_NUMBER_CHECKBOX, NUMBER, PERCENTAGE } from 'common/constants/gridType';
import { getChartColor } from './chartColors';
import ChartPopover from './ChartPopover';
import { WithChartProps } from './WithChart.props';
import { classes, Root } from './WithChart.styles';

const NUMERIC_GRID_TYPES = [NUMBER, PERCENTAGE, GRID_NUMBER_CHECKBOX];

const withChart = (WrappedComponent: ComponentType<any>) => {
  const WithChart = (props: WithChartProps) => {
    const { cell, selected } = props;
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const rootRef = useRef<HTMLDivElement>(null);

    const isNumericWithChart = useMemo(
      () =>
        !!cell.showChart &&
        NUMERIC_GRID_TYPES.includes(cell.gridType as string) &&
        !isNaN(Number(cell.value)),
      [cell.showChart, cell.gridType, cell.value]
    );

    const chartColor = useMemo(
      () => getChartColor(cell.chartTitle ?? cell.alias ?? undefined),
      [cell.chartTitle, cell.alias]
    );

    const openChart = useCallback(() => {
      if (rootRef.current) {
        setAnchorEl(rootRef.current);
      }
    }, []);

    const handleIconClick = useCallback((e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      setAnchorEl(e.currentTarget);
    }, []);

    const handleClose = useCallback(() => {
      setAnchorEl(null);
    }, []);

    // Shift+Space hotkey when cell is selected
    useEffect(() => {
      if (!isNumericWithChart || !selected) {
        return undefined;
      }

      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.shiftKey && e.key === ' ') {
          e.preventDefault();
          openChart();
        }
      };

      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isNumericWithChart, selected, openChart]);

    // Middle mouse click on the cell
    const handleAuxClick = useCallback(
      (e: React.MouseEvent) => {
        if (e.button === 1) {
          e.preventDefault();
          openChart();
        }
      },
      [openChart]
    );

    if (!isNumericWithChart) {
      return <WrappedComponent {...props} />;
    }

    return (
      <Root ref={rootRef} className={classes.root} onAuxClick={handleAuxClick}>
        <button
          type="button"
          className={classes.iconContainer}
          onClick={handleIconClick}
          aria-label="Show chart">
          <ShowChartOutlined sx={{ fontSize: 16, color: 'primary.main' }} />
        </button>
        <WrappedComponent {...props} />
        <ChartPopover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={handleClose}
          title={cell.chartTitle ?? cell.alias ?? undefined}
          color={chartColor}
          entityName={cell.chartEntityName}
          metricKey={cell.chartMetricKey}
        />
      </Root>
    );
  };

  return WithChart;
};

export default withChart;
